import type { ReactElement } from 'react'
import type { Organisation } from '../../types'
import {
  AccessTierField,
  DateField,
  FeaturesField,
  InvoiceOrReferenceField,
  NumericField,
  OrgSelector,
  SubmitButton,
} from './license-criteria-manual-form.parts'
import type { FormLookups } from './license-criteria-form-lookups'
import type { CriteriaFormState } from './use-license-criteria-form'

interface CriteriaFormFieldsProps {
  form: CriteriaFormState
  lookups: FormLookups
  organisations: Organisation[]
}

function buildToggleFeature(form: CriteriaFormState): (value: string, checked: boolean) => void {
  return (value: string, checked: boolean): void => {
    if (checked) {
      form.setSelectedFeatures([...form.selectedFeatures, value])
    } else {
      form.setSelectedFeatures(form.selectedFeatures.filter((v) => v !== value))
    }
  }
}

interface NumericDateFieldsProps {
  form: CriteriaFormState
}

function NumericDateFields({ form }: NumericDateFieldsProps): ReactElement {
  return (
    <>
      <NumericField
        labelKey="licensing.seatsGranted"
        value={form.formData.seatsGranted}
        min={1}
        required
        onChange={(value) => {
          form.setFormData({ ...form.formData, seatsGranted: value })
        }}
      />
      <NumericField
        labelKey="licensing.storageVolume"
        value={form.formData.volumeGrantedGb ?? 0}
        min={0}
        onChange={(value) => {
          form.setFormData({ ...form.formData, volumeGrantedGb: value })
        }}
      />
      <DateField
        labelKey="licensing.validFrom"
        value={form.formData.validFrom}
        required
        onChange={(value) => {
          form.setFormData({ ...form.formData, validFrom: value })
        }}
      />
      <DateField
        labelKey="licensing.validUntil"
        hint="licensing.perpetualLicenseHint"
        value={form.formData.validUntil ?? ''}
        onChange={(value) => {
          form.setFormData({ ...form.formData, validUntil: value })
        }}
      />
    </>
  )
}

export function CriteriaFormFields({
  form,
  lookups,
  organisations,
}: CriteriaFormFieldsProps): ReactElement {
  return (
    <>
      <OrgSelector
        organisations={organisations}
        organisationId={form.formData.organisationId}
        hasOrphanedInvoices={form.hasOrphanedInvoices}
        hasSelectedInvoice={form.hasSelectedInvoice}
        onChange={(value) => {
          form.setFormData({ ...form.formData, organisationId: value })
        }}
      />
      <InvoiceOrReferenceField
        hasOrgSelected={form.hasOrgSelected}
        availableInvoices={form.availableInvoices}
        useCustomReference={form.useCustomReference}
        selectedInvoiceId={form.selectedInvoiceId}
        externalReference={form.formData.externalReference ?? ''}
        onSelectInvoice={form.handleInvoiceSelect}
        onSwitchToCustom={() => {
          form.setUseCustomReference(true)
          form.setSelectedInvoiceId('')
          form.setFormData({ ...form.formData, externalReference: '' })
        }}
        onChangeReference={(value) => {
          form.setFormData({ ...form.formData, externalReference: value })
        }}
        onSwitchToList={() => {
          form.setUseCustomReference(false)
        }}
      />
      <AccessTierField
        value={form.formData.accessTier}
        options={lookups.ACCESS_TIERS}
        onChange={(value) => {
          form.setFormData({ ...form.formData, accessTier: value })
        }}
      />
      <NumericDateFields form={form} />
      <FeaturesField
        options={lookups.AVAILABLE_FEATURES}
        selected={form.selectedFeatures}
        onToggle={buildToggleFeature(form)}
      />
      <SubmitButton
        disabled={form.isSubmitting || !form.canSubmit}
        submitting={form.isSubmitting}
      />
    </>
  )
}
